import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { AIDropdownMinimal } from '@app/components/AIDropdown/AIDropdownMinimal';
import { VariableDropdown } from '@app/components/VariableDropdown/VariableDropdown';
import { 
  improveText, 
  fixGrammarAndSpelling, 
  makeTextConcise, 
  regenerateText,
  changeTone
} from '@app/services/service';
import ChangeToneIcon from '@app/assets/icons/ai/change-tone.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';
import GrammarIcon from '@app/assets/icons/ai/grammar.svg?react';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import Edit03Icon from '@app/assets/icons/edit-03.svg?react';
import goodReviewSvgUrl from '@app/assets/illustrations/good-review.svg';
import emptyLogo from '@app/assets/images/empty-logo.png';

interface PositiveWindowContent {
  heading: string;
  description: string;
  imageUrl: string;
  additionalPlatforms: {
    google: boolean;
    yelp: boolean;
  };
  additionalPlatformsTitle: string;
  additionalPlatformsDescription: string;
  buttonsColor: string;
}

// Helper function to insert variable at cursor position
const insertVariableAtCursor = (
  currentValue: string,
  variable: string,
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
): { newValue: string; cursorPosition: number } => {
  if (inputRef.current) {
    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = currentValue.slice(0, start) + variable + currentValue.slice(end);
    const cursorPosition = start + variable.length;
    return { newValue, cursorPosition };
  }
  const newValue = currentValue + variable;
  return { newValue, cursorPosition: newValue.length };
};

const defaultPositiveWindowContent: PositiveWindowContent = {
  heading: 'Thank you for your review',
  description: 'We make it easy for your to share your exact review on our other platforms, just click the buttons below, it helps us out tremendously!',
  imageUrl: goodReviewSvgUrl,
  additionalPlatforms: {
    google: true,
    yelp: false
  },
  additionalPlatformsTitle: 'Leave the same review on another platforms',
  additionalPlatformsDescription: 'You don\'t need to re-enter anything',
  buttonsColor: '#3B82F6'
};

interface PositiveWindowDesignEditorProps {
  content: PositiveWindowContent;
  onChange: (content: PositiveWindowContent) => void;
}

export const PositiveWindowDesignEditor = ({ content, onChange }: PositiveWindowDesignEditorProps) => {
  const headingRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const titleDescriptionRef = useRef<HTMLInputElement>(null);

  const handleHeadingVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(content.heading, variable, headingRef);
    onChange({ ...content, heading: newValue });
    setTimeout(() => {
      if (headingRef.current) {
        headingRef.current.setSelectionRange(cursorPosition, cursorPosition);
        headingRef.current.focus();
      }
    }, 0);
  };

  const handleDescriptionVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(content.description, variable, descriptionRef);
    onChange({ ...content, description: newValue });
    setTimeout(() => {
      if (descriptionRef.current) {
        descriptionRef.current.setSelectionRange(cursorPosition, cursorPosition);
        descriptionRef.current.focus();
      }
    }, 0);
  };

  const handleTitleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(content.additionalPlatformsTitle, variable, titleRef);
    onChange({ ...content, additionalPlatformsTitle: newValue });
    setTimeout(() => {
      if (titleRef.current) {
        titleRef.current.setSelectionRange(cursorPosition, cursorPosition);
        titleRef.current.focus();
      }
    }, 0);
  };

  const handleTitleDescriptionVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(content.additionalPlatformsDescription, variable, titleDescriptionRef);
    onChange({ ...content, additionalPlatformsDescription: newValue });
    setTimeout(() => {
      if (titleDescriptionRef.current) {
        titleDescriptionRef.current.setSelectionRange(cursorPosition, cursorPosition);
        titleDescriptionRef.current.focus();
      }
    }, 0);
  };

  // Load saved content when component mounts
  useEffect(() => {
    const storedData = localStorage.getItem('campaignFormData');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.positiveWindowDesignContent) {
          // Merge with default values to ensure all new fields exist
          const savedContent = {
            ...defaultPositiveWindowContent,
            ...parsedData.positiveWindowDesignContent
          };
          // Explicitly preserve imageUrl from saved data if it exists (including empty string for defaults)
          if ('imageUrl' in parsedData.positiveWindowDesignContent) {
            savedContent.imageUrl = parsedData.positiveWindowDesignContent.imageUrl;
          }
          onChange(savedContent);
        }
      } catch (error) {
        // If parsing fails, keep default values
        console.error('Error parsing stored positive window data:', error);
      }
    }
  }, [onChange]);

  const handleAIAction = async (action: () => Promise<any>, field: 'heading' | 'description' | 'additionalPlatformsTitle' | 'additionalPlatformsDescription') => {
    const value = content[field];
    if (!value.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        onChange({ ...content, [field]: response.data.text });
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const createToneOptions = (field: 'heading' | 'description' | 'additionalPlatformsTitle' | 'additionalPlatformsDescription') => {
    const tones = [
      { label: 'Write appreciative', tone: 'appreciative' },
      { label: 'Write apologetic', tone: 'apologetic' },
      { label: 'Write informative', tone: 'informative' },
      { label: 'Write interactive', tone: 'interactive' },
      { label: 'Write humorous', tone: 'humorous' },
      { label: 'Write formal', tone: 'formal' },
      { label: 'Write casual', tone: 'casual' },
      { label: 'Write personalized', tone: 'personalized' }
    ];

    return tones.map(({ label, tone }) => ({
      label,
      Icon: <Edit03Icon className="w-4 h-4" />,
      onClick: () => handleAIAction(() => changeTone(content[field], tone), field)
    }));
  };

  const createAIActions = (field: 'heading' | 'description' | 'additionalPlatformsTitle' | 'additionalPlatformsDescription') => [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(content[field]), field)
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(field)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(content[field]), field)
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(content[field]), field)
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(content[field]), field)
    }
  ];

  const handlePlatformToggle = (platform: keyof PositiveWindowContent['additionalPlatforms']) => {
    onChange({
      ...content,
      additionalPlatforms: {
        ...content.additionalPlatforms,
        [platform]: !content.additionalPlatforms[platform]
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Heading Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
        <div className="flex items-start gap-2">
          <input
            ref={headingRef}
            type="text"
            value={content.heading}
            onChange={(e) => onChange({ ...content, heading: e.target.value })}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Thank you for your review"
          />
          <div className="flex gap-1 items-center">
            <VariableDropdown onSelect={handleHeadingVariableSelect} />
            <div className="relative">
              <AIDropdownMinimal     
                actions={createAIActions('heading')}
                onlyIcon={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Description Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <div className="flex items-start gap-2">
          <input
            ref={descriptionRef}
            type="text"
            value={content.description}
            onChange={(e) => onChange({ ...content, description: e.target.value })}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="We make it easy for your to share your exact review on our other platforms, just click the buttons below, it helps us out tremendously!"
          />
          <div className="flex gap-1 items-center">
            <VariableDropdown onSelect={handleDescriptionVariableSelect} />
            <div className="relative">
              <AIDropdownMinimal     
                actions={createAIActions('description')}
                onlyIcon={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Image Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
        <div className="flex items-center gap-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg w-36 h-24 flex items-center justify-center overflow-hidden bg-white">
            {content.imageUrl && content.imageUrl.startsWith('data:') ? (
              <img src={content.imageUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <img src={emptyLogo} alt="Empty logo" className="w-full h-full object-cover" />
            )}
          </div>
          <button 
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    onChange({ ...content, imageUrl: e.target?.result as string });
                  };
                  reader.readAsDataURL(file);
                }
              };
              input.click();
            }}
            className="px-4 py-2 bg-[#E7EAF4] text-gray-700 rounded-lg transition-colors"
          >
            Choose photo
          </button>
        </div>
      </div>

      {/* Divider Line */}
      <div className="border-b border-gray-200 mt-4 mb-6"></div>

      {/* Additional Platforms Section */}
      <div>
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Additional review platforms are all platforms that are not set by default. The user can choose whether to leave a review on them or not.
          </p>
          <p className="text-sm text-blue-600 font-medium">Select up to 4 platforms for review</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="flex items-center">
              <input 
                type="checkbox" 
                checked={content.additionalPlatforms.google}
                onChange={() => handlePlatformToggle('google')}
                className="mr-2" 
              />
              <span className="text-sm">Google</span>
            </label>
          </div>
          
          <div className="space-y-2">
            <label className="flex items-center">
              <input 
                type="checkbox" 
                checked={content.additionalPlatforms.yelp}
                onChange={() => handlePlatformToggle('yelp')}
                className="mr-2" 
              />
              <span className="text-sm">Yelp</span>
            </label>
          </div>
        </div>
      </div>

      {/* Title for Additional Platforms */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title (for additional platforms section)</label>
        <div className="flex items-start gap-2">
          <input
            ref={titleRef}
            type="text"
            value={content.additionalPlatformsTitle}
            onChange={(e) => onChange({ ...content, additionalPlatformsTitle: e.target.value })}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Leave the same review on another platforms"
          />
          <div className="flex gap-1 items-center">
            <VariableDropdown onSelect={handleTitleVariableSelect} />
            <div className="relative">
              <AIDropdownMinimal     
                actions={createAIActions('additionalPlatformsTitle')}
                onlyIcon={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Description for Additional Platforms */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description (for additional platforms section)</label>
        <div className="flex items-start gap-2">
          <input
            ref={titleDescriptionRef}
            type="text"
            value={content.additionalPlatformsDescription}
            onChange={(e) => onChange({ ...content, additionalPlatformsDescription: e.target.value })}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="You don't need to re-enter anything"
          />
          <div className="flex gap-1 items-center">
            <VariableDropdown onSelect={handleTitleDescriptionVariableSelect} />
            <div className="relative">
              <AIDropdownMinimal     
                actions={createAIActions('additionalPlatformsDescription')}
                onlyIcon={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Buttons Color Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Buttons color</label>
        <div className="space-y-3">
          <input
            type="text"
            value={content.buttonsColor}
            onChange={(e) => onChange({ ...content, buttonsColor: e.target.value })}
            placeholder="Custom #HEX"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          />
          <div className="flex gap-2 flex-wrap">
            {['#3B82F6', '#000000', '#00B2FF', '#28a745', '#ffc107', '#dc3545', '#e83e8c', '#6f42c1', '#ffffff'].map((color) => (
              <button
                key={color}
                onClick={() => onChange({ ...content, buttonsColor: color })}
                className={clsx(
                  'w-8 h-8 border-2 transition-colors rounded-md',
                  content.buttonsColor === color ? 'border-blue-500' : 'border-gray-300'
                )}
                style={{ backgroundColor: color }}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
